import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";

interface GDPRConsentProps {
  onConsent: () => void;
  isLoading?: boolean;
}

const GDPRConsent: React.FC<GDPRConsentProps> = ({ onConsent, isLoading = false }) => {
  const [informationRead, setInformationRead] = useState(false);
  const [processingConsent, setProcessingConsent] = useState(false);
  const [withdrawalAware, setWithdrawalAware] = useState(false);

  const allConsentsGiven = informationRead && processingConsent && withdrawalAware;

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-center">
          Avtal om behandling av personuppgifter – Fritidskompassen
        </CardTitle>
        <CardDescription className="text-center">
          För att kunna använda Fritidskompassen krävs att du godkänner vår behandling av personuppgifter enligt GDPR.
          Du kommer också att behöva fylla i några obligatoriska uppgifter i din profil.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <ScrollArea className="h-64 w-full border rounded-md p-4">
          <div className="space-y-4 text-sm">
            <div>
              <h3 className="font-semibold text-foreground mb-2">1. Information</h3>
              <p className="text-muted-foreground mb-2">
                Vi behandlar personuppgifter för att planera och genomföra fritidsaktiviteter på ett tryggt och säkert sätt.
              </p>
              <p className="text-muted-foreground mb-2">
                De uppgifter som behandlas är: namn, födelsedatum, kontaktuppgifter, kön, allergier, medicinska behov, 
                färdtjänst, rullstolsanvändning, godkännande av fotografering och annan information du själv väljer att lämna.
              </p>
              <p className="text-muted-foreground">
                Uppgifterna används endast för att underlätta planering, säkerhet och anpassning av verksamheten.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">2. Rättslig grund</h3>
              <p className="text-muted-foreground mb-2">
                Behandling av hälsouppgifter sker enbart med stöd av ditt uttryckliga samtycke.
              </p>
              <p className="text-muted-foreground">
                Samtycket är en förutsättning för att kunna skapa och använda ett konto i Fritidskompassen.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">3. Lagring och radering</h3>
              <p className="text-muted-foreground mb-2">
                Uppgifterna sparas endast så länge du deltar i verksamheten.
              </p>
              <p className="text-muted-foreground">
                När ditt konto avslutas raderas all information.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">4. Tillgång</h3>
              <p className="text-muted-foreground mb-2">
                Endast behörig personal inom verksamheten har tillgång till uppgifterna.
              </p>
              <p className="text-muted-foreground">
                Uppgifter lämnas aldrig vidare till tredje part.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">5. Dina rättigheter</h3>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li>Du har rätt att få veta vilka uppgifter vi sparar om dig.</li>
                <li>Du har rätt att få felaktiga uppgifter rättade.</li>
                <li>Du har rätt att när som helst återkalla ditt samtycke.</li>
                <li>Du har rätt att få alla dina uppgifter raderade ("rätten att bli bortglömd").</li>
              </ul>
            </div>
          </div>
        </ScrollArea>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Nästa steg efter samtycke:</strong> Du kommer att dirigeras till din profil för att fylla i obligatoriska uppgifter som är markerade med en röd stjärna (<span className="text-[#ea384c]">*</span>). Detta hjälper oss att skapa en bättre upplevelse för dig och säkerställa att du får tillgång till rätt aktiviteter.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="information-read"
              checked={informationRead}
              onCheckedChange={(checked) => setInformationRead(checked as boolean)}
            />
            <label
              htmlFor="information-read"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Jag har tagit del av informationen ovan.
            </label>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="processing-consent"
              checked={processingConsent}
              onCheckedChange={(checked) => setProcessingConsent(checked as boolean)}
            />
            <label
              htmlFor="processing-consent"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Jag godkänner att Fritidskompassen behandlar mina personuppgifter enligt avtalet.
            </label>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="withdrawal-aware"
              checked={withdrawalAware}
              onCheckedChange={(checked) => setWithdrawalAware(checked as boolean)}
            />
            <label
              htmlFor="withdrawal-aware"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Jag är medveten om att jag kan återkalla samtycket när som helst.
            </label>
          </div>
        </div>

        <Button
          onClick={onConsent}
          disabled={!allConsentsGiven || isLoading}
          className="w-full"
        >
          {isLoading ? "Sparar samtycke..." : "Godkänn och fortsätt"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default GDPRConsent;